import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatTenantDateTime } from '../../lib/dates'

type PaymentType = 'prepayment' | 'topup' | 'refund' | 'cashout'
type PaymentMethod = 'cash' | 'card' | 'transfer'

interface PaymentRow {
  id: string
  createdAt: string
  amount: number
  type: PaymentType
  paymentMethod: PaymentMethod
  note: string | null
  isOwnerCorrection: boolean
  shiftId: string | null
  rentalId: string | null
  rental: { id: string; customerName: string | null; bikeCodes: string | null } | null
  manager: { id: string; name: string }
  point: { id: string; name: string }
}

interface ManagerRow { id: string; name: string; isActive: boolean }

const TYPE_LABEL: Record<PaymentType, string> = {
  prepayment: 'Предоплата',
  topup: 'Оплата',
  refund: 'Возврат',
  cashout: 'Инкассация',
}

const METHOD_LABEL: Record<PaymentMethod, string> = {
  cash: '💵 Нал',
  card: '💳 Безнал',
  transfer: '📲 Перевод',
}

const fmt = (n: number) => n.toLocaleString('ru-RU') + ' ₽'

export default function PaymentsPage() {
  const today = new Date()
  const monthAgo = new Date(today); monthAgo.setDate(monthAgo.getDate() - 30)
  const [from, setFrom] = useState(monthAgo.toISOString().slice(0, 10))
  const [to, setTo] = useState(today.toISOString().slice(0, 10))
  const [managerId, setManagerId] = useState<string>('')
  const [type, setType] = useState<PaymentType | ''>('')
  const [method, setMethod] = useState<PaymentMethod | ''>('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const limit = 50

  const { data: managersData } = useQuery<{ managers: ManagerRow[] }>({
    queryKey: ['managers'],
    queryFn: () => api.get('/users').then((r) => r.data),
  })
  const managers = managersData?.managers ?? []

  const { data, isLoading } = useQuery<{ payments: PaymentRow[]; total: number; page: number; limit: number }>({
    queryKey: ['payments-list', from, to, managerId, type, method, search, page],
    queryFn: () => api.get('/payments', {
      params: {
        from: new Date(from).toISOString(),
        to: new Date(to + 'T23:59:59').toISOString(),
        ...(managerId ? { managerId } : {}),
        ...(type ? { type } : {}),
        ...(method ? { paymentMethod: method } : {}),
        ...(search.trim().length >= 2 ? { search: search.trim() } : {}),
        page,
        limit,
      },
    }).then((r) => r.data),
    staleTime: 30_000,
  })

  const payments = data?.payments ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const startIdx = total === 0 ? 0 : (page - 1) * limit + 1
  const endIdx = Math.min(page * limit, total)

  const editing = editingId ? payments.find((p) => p.id === editingId) ?? null : null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Платежи</h1>
          <span className="text-xs text-gray-500">Всего: {total}</span>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-brand-600 text-white text-sm px-4 py-2 rounded-xl font-medium hover:bg-brand-700"
        >
          + Создать платёж
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-3 grid grid-cols-2 md:grid-cols-6 gap-2">
        <label className="text-xs text-gray-600 col-span-1">
          С
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1) }}
            className="w-full mt-1 border rounded-lg px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs text-gray-600 col-span-1">
          По
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1) }}
            className="w-full mt-1 border rounded-lg px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs text-gray-600 col-span-1">
          Менеджер
          <select value={managerId} onChange={(e) => { setManagerId(e.target.value); setPage(1) }}
            className="w-full mt-1 border rounded-lg px-2 py-1.5 text-sm">
            <option value="">Все</option>
            {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-600 col-span-1">
          Тип
          <select value={type} onChange={(e) => { setType(e.target.value as PaymentType | ''); setPage(1) }}
            className="w-full mt-1 border rounded-lg px-2 py-1.5 text-sm">
            <option value="">Все</option>
            <option value="prepayment">Предоплата</option>
            <option value="topup">Оплата</option>
            <option value="refund">Возврат</option>
            <option value="cashout">Инкассация</option>
          </select>
        </label>
        <label className="text-xs text-gray-600 col-span-1">
          Способ
          <select value={method} onChange={(e) => { setMethod(e.target.value as PaymentMethod | ''); setPage(1) }}
            className="w-full mt-1 border rounded-lg px-2 py-1.5 text-sm">
            <option value="">Все</option>
            <option value="cash">Нал</option>
            <option value="card">Безнал</option>
            <option value="transfer">Перевод</option>
          </select>
        </label>
        <label className="text-xs text-gray-600 col-span-1">
          Поиск
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            placeholder="код байка, имя, телефон, заметка"
            className="w-full mt-1 border rounded-lg px-2 py-1.5 text-sm" />
        </label>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Дата</th>
                <th className="text-left px-3 py-2">Тип</th>
                <th className="text-right px-3 py-2">Сумма</th>
                <th className="text-left px-3 py-2">Способ</th>
                <th className="text-left px-3 py-2">Менеджер</th>
                <th className="text-left px-3 py-2">Точка</th>
                <th className="text-left px-3 py-2">Аренда</th>
                <th className="text-left px-3 py-2">Заметка</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-500">Загрузка…</td></tr>
              )}
              {!isLoading && payments.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-500">Платежей за период нет</td></tr>
              )}
              {payments.map((p) => {
                const signed = p.type === 'refund' || p.type === 'cashout' ? -p.amount : p.amount
                const amountColor = signed < 0 ? 'text-red-600' : 'text-gray-900'
                return (
                  <tr key={p.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{formatTenantDateTime(p.createdAt)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="text-xs">{TYPE_LABEL[p.type]}</span>
                      {p.isOwnerCorrection && (
                        <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">корр.</span>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-right font-medium ${amountColor}`}>{fmt(signed)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">{METHOD_LABEL[p.paymentMethod]}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">{p.manager.name}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">{p.point.name}</td>
                    <td className="px-3 py-2 text-xs">
                      {p.rental ? (
                        <span>
                          {p.rental.bikeCodes ? <span className="font-mono">#{p.rental.bikeCodes}</span> : '—'}
                          {p.rental.customerName && <span className="text-gray-500"> · {p.rental.customerName}</span>}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600 max-w-[200px] truncate" title={p.note ?? ''}>{p.note ?? ''}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => setEditingId(p.id)}
                        className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
                        ✏️ Править
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-3 py-2 border-t bg-gray-50 text-xs text-gray-600">
          <span>Показано {startIdx}–{endIdx} из {total}</span>
          <div className="flex gap-1">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-2 py-1 rounded border border-gray-300 bg-white disabled:opacity-40">← Назад</button>
            <span className="px-2 py-1">{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
              className="px-2 py-1 rounded border border-gray-300 bg-white disabled:opacity-40">Вперёд →</button>
          </div>
        </div>
      </div>

      {editing && (
        <EditPaymentModal
          payment={editing}
          onClose={() => setEditingId(null)}
        />
      )}
      {showCreate && (
        <CreatePaymentModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false)
            setPage(1)
          }}
        />
      )}
    </div>
  )
}

function EditPaymentModal({ payment, onClose }: { payment: PaymentRow; onClose: () => void }) {
  const qc = useQueryClient()
  const [amount, setAmount] = useState<string>(String(payment.amount))
  const [type, setType] = useState<PaymentType>(payment.type)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(payment.paymentMethod)
  const [note, setNote] = useState<string>(payment.note ?? '')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => api.patch(`/payments/${payment.id}`, {
      amount: Number(amount),
      type,
      paymentMethod,
      note: note.trim() || null,
      reason: reason.trim(),
    }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payments-list'] })
      qc.invalidateQueries({ queryKey: ['shifts'] })
      qc.invalidateQueries({ queryKey: ['journal'] })
      qc.invalidateQueries({ queryKey: ['rentals'] })
      onClose()
    },
    onError: (e: { response?: { data?: { error?: { message?: string } } } }) => {
      setError(e?.response?.data?.error?.message ?? 'Не удалось сохранить')
    },
  })

  const amtNum = parseFloat(amount)
  const amountOk = !isNaN(amtNum) && amtNum >= 0
  const reasonOk = reason.trim().length >= 5
  const canSubmit = !mutation.isPending && amountOk && reasonOk

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h2 className="font-semibold">Редактировать платёж</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
          Создан: {formatTenantDateTime(payment.createdAt)} · {payment.manager.name} · {payment.point.name}
          {payment.rental?.bikeCodes && <> · <span className="font-mono">#{payment.rental.bikeCodes}</span></>}
        </div>

        <label className="block text-xs text-gray-600">
          Сумма (₽)
          <input type="number" min={0} step="0.01" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>

        <label className="block text-xs text-gray-600">
          Тип
          <select value={type} onChange={(e) => setType(e.target.value as PaymentType)}
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
            <option value="prepayment">Предоплата</option>
            <option value="topup">Оплата</option>
            <option value="refund">Возврат</option>
            <option value="cashout">Инкассация</option>
          </select>
        </label>

        <label className="block text-xs text-gray-600">
          Способ
          <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
            <option value="cash">💵 Нал</option>
            <option value="card">💳 Безнал</option>
            <option value="transfer">📲 Перевод</option>
          </select>
        </label>

        <label className="block text-xs text-gray-600">
          Заметка
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="(необязательно)"
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>

        <label className="block text-xs text-gray-600">
          <span className="flex justify-between">
            <span>Причина изменения <span className="text-red-500">*</span></span>
            <span className={reason.trim().length >= 5 ? 'text-green-600' : 'text-gray-400'}>
              {reason.trim().length}/5
            </span>
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)}
            placeholder="Зачем правим — для аудита (минимум 5 символов)"
            rows={2}
            className={`w-full mt-1 border rounded-lg px-2 py-2 text-sm ${reason.trim().length > 0 && !reasonOk ? 'border-red-300' : ''}`}
          />
        </label>

        {error && <div className="text-xs text-red-600 bg-red-50 rounded p-2">{error}</div>}

        {!canSubmit && !mutation.isPending && (
          <p className="text-xs text-amber-600">
            {!amountOk && 'Укажите корректную сумму. '}
            {!reasonOk && `Причина: нужно ещё ${Math.max(0, 5 - reason.trim().length)} симв.`}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-gray-300 text-sm">Отмена</button>
          <button onClick={() => { setError(null); mutation.mutate() }} disabled={!canSubmit}
            className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50">
            {mutation.isPending ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  )
}

const TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'prepayment', label: 'Предоплата' },
  { value: 'topup', label: 'Оплата' },
  { value: 'refund', label: 'Возврат' },
  { value: 'cashout', label: 'Инкассация' },
]

function CreatePaymentModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const qc = useQueryClient()
  const [rentalSearch, setRentalSearch] = useState('')
  const [rentalId, setRentalId] = useState<string>('')
  const [shiftId, setShiftId] = useState<string>('')
  const [pointId, setPointId] = useState<string>('')
  const [type, setType] = useState<string>('topup')
  const [paymentMethod, setPaymentMethod] = useState<string>('cash')
  const [amount, setAmount] = useState<string>('')
  const [note, setNote] = useState<string>('')
  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().slice(0, 16))
  const [error, setError] = useState<string | null>(null)

  const { data: pointsData } = useQuery({ queryKey: ['points'], queryFn: () => api.get('/points').then((r) => r.data) })
  const points: { id: string; name: string }[] = pointsData?.points ?? []

  const { data: shiftsData } = useQuery({
    queryKey: ['shifts-owner-list'],
    queryFn: () => api.get('/shifts', { params: { limit: 100 } }).then((r) => r.data),
  })
  const shifts: { id: string; openedAt: string; closedAt?: string | null; status: string; point: { id: string; name: string } }[] = shiftsData?.shifts ?? []

  const { data: rentalsData } = useQuery({
    queryKey: ['rentals-search', rentalSearch],
    queryFn: () => rentalSearch.trim().length >= 2
      ? api.get('/rentals', { params: { limit: 10 } }).then((r) => r.data)
      : Promise.resolve({ rentals: [] }),
    enabled: rentalSearch.trim().length >= 2,
  })
  const foundRentals: { id: string; bike?: { code: string } | null; lines?: { bike: { code: string } }[]; customerName?: string }[] = rentalsData?.rentals ?? []

  const amtNum = parseFloat(amount)
  const canSubmit = !isNaN(amtNum) && amtNum > 0 && !!pointId && note.trim().length >= 5

  const mutation = useMutation({
    mutationFn: () => api.post('/payments/owner-create', {
      rentalId: rentalId || undefined,
      shiftId: shiftId || undefined,
      pointId,
      type,
      paymentMethod,
      amount: amtNum,
      note: note.trim(),
      paymentDate: paymentDate ? new Date(paymentDate).toISOString() : undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payments-list'] })
      qc.invalidateQueries({ queryKey: ['rentals'] })
      qc.invalidateQueries({ queryKey: ['shifts'] })
      onCreated()
    },
    onError: (e: { response?: { data?: { error?: { message?: string } } } }) => {
      setError(e?.response?.data?.error?.message ?? 'Не удалось создать платёж')
    },
  })

  const fmtShift = (s: typeof shifts[0]) => {
    const d = new Date(s.openedAt).toLocaleDateString('ru', { day: '2-digit', month: '2-digit' })
    return `${s.point.name} · ${d} · ${s.status === 'open' ? 'открыта' : 'закрыта'}`
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h2 className="font-semibold text-lg">Создать платёж</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>

        <label className="block text-xs text-gray-600">
          Аренда (необязательно — введите код велика или имя)
          <input value={rentalSearch} onChange={(e) => { setRentalSearch(e.target.value); if (!e.target.value) setRentalId('') }}
            placeholder="Код велика или имя клиента"
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>
        {foundRentals.length > 0 && !rentalId && (
          <div className="border rounded-lg divide-y max-h-32 overflow-y-auto text-sm">
            {foundRentals.map((r) => {
              const codes = (r.lines && r.lines.length > 0)
                ? r.lines.map((l) => `#${l.bike.code}`).join(', ')
                : r.bike ? `#${r.bike.code}` : '—'
              const label = `${codes}${r.customerName ? ` · ${r.customerName}` : ''}`
              return (
                <button key={r.id} type="button"
                  onClick={() => { setRentalId(r.id); setRentalSearch(label) }}
                  className="w-full text-left px-3 py-2 hover:bg-brand-50">
                  {label}
                </button>
              )
            })}
          </div>
        )}
        {rentalId && (
          <div className="flex items-center gap-2 text-xs text-brand-700 bg-brand-50 rounded-lg px-3 py-1.5">
            <span>Аренда выбрана</span>
            <button type="button" onClick={() => { setRentalId(''); setRentalSearch('') }} className="text-brand-400 ml-auto">✕</button>
          </div>
        )}

        <label className="block text-xs text-gray-600">
          Точка <span className="text-red-500">*</span>
          <select value={pointId} onChange={(e) => setPointId(e.target.value)}
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
            <option value="">— выберите точку —</option>
            {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>

        <label className="block text-xs text-gray-600">
          Смена (необязательно)
          <select value={shiftId} onChange={(e) => setShiftId(e.target.value)}
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
            <option value="">— без привязки к смене —</option>
            {shifts.map((s) => <option key={s.id} value={s.id}>{fmtShift(s)}</option>)}
          </select>
        </label>

        <label className="block text-xs text-gray-600">
          Дата и время платежа
          <input type="datetime-local" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs text-gray-600">
            Тип
            <select value={type} onChange={(e) => setType(e.target.value)}
              className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
              {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="block text-xs text-gray-600">
            Способ
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
              <option value="cash">💵 Нал</option>
              <option value="card">💳 Безнал</option>
              <option value="transfer">📲 Перевод</option>
            </select>
          </label>
        </div>

        <label className="block text-xs text-gray-600">
          Сумма, ₽ <span className="text-red-500">*</span>
          <input type="number" min={0.01} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>

        <label className="block text-xs text-gray-600">
          <span className="flex justify-between">
            <span>Причина / комментарий <span className="text-red-500">*</span></span>
            <span className={note.trim().length >= 5 ? 'text-green-600' : 'text-gray-400'}>{note.trim().length}/5</span>
          </span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Зачем платёж — для аудита (минимум 5 символов)" rows={2}
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>

        {error && <div className="text-xs text-red-600 bg-red-50 rounded p-2">{error}</div>}

        {!canSubmit && !mutation.isPending && (amount || note || pointId) && (
          <p className="text-xs text-amber-600">
            {!pointId && 'Выберите точку. '}
            {(isNaN(amtNum) || amtNum <= 0) && 'Укажите сумму > 0. '}
            {note.trim().length < 5 && `Причина: нужно ещё ${Math.max(0, 5 - note.trim().length)} симв.`}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-gray-300 text-sm">Отмена</button>
          <button onClick={() => { setError(null); mutation.mutate() }} disabled={!canSubmit || mutation.isPending}
            className="flex-1 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium disabled:opacity-50">
            {mutation.isPending ? 'Создание…' : 'Создать платёж'}
          </button>
        </div>
      </div>
    </div>
  )
}
